import { useEffect } from 'react'
import { CheckCircle2, XCircle, Info, X } from 'lucide-react'

export type ToastType = 'success' | 'error' | 'info'

export interface ToastAction {
  label: string
  onClick: () => void
}

export interface ToastProps {
  message: string
  type: ToastType
  onClose: () => void
  /** Auto-dismiss delay in ms. Pass 0 to disable auto-dismiss (use for action toasts). */
  duration?: number
  /** Optional inline action button rendered alongside the message. */
  action?: ToastAction
}

export function Toast({ message, type, onClose, duration = 3000, action }: ToastProps) {
  useEffect(() => {
    // duration=0 means no auto-dismiss (caller is responsible for closing)
    if (duration === 0) return
    const timer = setTimeout(() => {
      onClose()
    }, duration)

    return () => clearTimeout(timer)
  }, [duration, onClose])

  const styles = {
    success: {
      bg: 'bg-emerald-500/10 border-emerald-500/20',
      text: 'text-emerald-400',
      icon: CheckCircle2,
    },
    error: {
      bg: 'bg-red-500/10 border-red-500/20',
      text: 'text-red-400',
      icon: XCircle,
    },
    info: {
      bg: 'bg-[#5E6AD2]/10 border-[#5E6AD2]/20',
      text: 'text-[#8B96E9]',
      icon: Info,
    },
  }[type]

  const Icon = styles.icon

  return (
    <div
      className={`${styles.bg} border backdrop-blur-sm px-4 py-3 rounded-xl shadow-lg flex items-center gap-3 min-w-[300px] max-w-md animate-in slide-in-from-bottom-2 duration-300`}
    >
      <Icon size={18} className={styles.text} />
      <p className={`flex-grow text-sm ${styles.text}`}>{message}</p>
      {action && (
        <button
          onClick={() => {
            action.onClick()
            onClose()
          }}
          className="shrink-0 text-xs font-medium px-2.5 py-1 rounded-md border border-current opacity-80 hover:opacity-100 transition-opacity"
          style={{ color: 'inherit' }}
        >
          {action.label}
        </button>
      )}
      <button
        onClick={onClose}
        className="p-1 rounded text-[#666666] hover:text-[#EEEEEE] hover:bg-white/5 transition-colors"
        aria-label="Close notification"
      >
        <X size={16} />
      </button>
    </div>
  )
}
